import { useCallback, useRef, useState, type ReactNode } from 'react';
import { GripHorizontal, GripVertical } from 'lucide-react';

export interface BooxPlannerSplitOption {
  value: string;
  label: string;
}

interface BooxPlannerRendererProps {
  eyebrow?: string;
  title: string;
  badges?: ReactNode;
  actions?: ReactNode;
  navigation?: ReactNode;
  mainTitle: string;
  main: ReactNode;
  mainFilter?: ReactNode;
  rightTitle?: string;
  rightFilter?: ReactNode;
  rightPanel?: ReactNode;
  bottomTitle?: string;
  bottomFilter?: ReactNode;
  bottomPanel?: ReactNode;
  footer?: ReactNode;
  splitOptions?: BooxPlannerSplitOption[];
  horizontalValue: string;
  verticalValue: string;
  onHorizontalValueChange: (value: string) => void;
  onVerticalValueChange: (value: string) => void;
  defaultRightSize?: number;
  defaultBottomSize?: number;
}

export function BooxPlannerRenderer({
  eyebrow,
  title,
  badges,
  actions,
  navigation,
  mainTitle,
  main,
  mainFilter,
  rightTitle,
  rightFilter,
  rightPanel,
  bottomTitle,
  bottomFilter,
  bottomPanel,
  footer,
  splitOptions = [{ value: 'blank', label: 'Blank' }],
  horizontalValue,
  verticalValue,
  onHorizontalValueChange,
  onVerticalValueChange,
  defaultRightSize = 420,
  defaultBottomSize = 320,
}: BooxPlannerRendererProps) {
  const [rightSize, setRightSize] = useState(defaultRightSize);
  const [bottomSize, setBottomSize] = useState(defaultBottomSize);
  const draggingRef = useRef(false);
  const hasRightPanel = horizontalValue !== 'blank' && Boolean(rightPanel);
  const hasBottomPanel = verticalValue !== 'blank' && Boolean(bottomPanel);

  const startRightDrag = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    draggingRef.current = true;
    const startX = event.clientX;
    const startSize = rightSize;

    const onMove = (moveEvent: MouseEvent) => {
      if (!draggingRef.current) return;
      setRightSize(Math.max(280, Math.min(860, startSize + startX - moveEvent.clientX)));
    };

    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [rightSize]);

  const startBottomDrag = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    draggingRef.current = true;
    const startY = event.clientY;
    const startSize = bottomSize;

    const onMove = (moveEvent: MouseEvent) => {
      if (!draggingRef.current) return;
      setBottomSize(Math.max(220, Math.min(720, startSize + startY - moveEvent.clientY)));
    };

    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [bottomSize]);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden border bg-background">
      <div className="z-30 flex shrink-0 items-center gap-2 border-b bg-background px-3 py-2">
        <div className="min-w-0">
          {eyebrow ? (
            <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">{eyebrow}</div>
          ) : null}
          <div className="truncate text-sm font-semibold text-foreground">{title}</div>
        </div>
        {badges ? <div className="hidden min-w-0 flex-1 flex-wrap gap-1.5 md:flex">{badges}</div> : <div className="flex-1" />}
        <div className="flex shrink-0 items-center gap-1">
          <label className="flex h-7 items-center overflow-hidden rounded-md border bg-background">
            <span className="border-r px-2 text-[10px] font-semibold text-muted-foreground" title="Horizontal split: right panel">
              H
            </span>
            <select
              className="h-full min-w-24 bg-background px-2 text-xs outline-none"
              value={horizontalValue}
              onChange={(event) => onHorizontalValueChange(event.target.value)}
              title="Horizontal split right panel"
            >
              {splitOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="flex h-7 items-center overflow-hidden rounded-md border bg-background">
            <span className="border-r px-2 text-[10px] font-semibold text-muted-foreground" title="Vertical split: left bottom panel">
              V
            </span>
            <select
              className="h-full min-w-24 bg-background px-2 text-xs outline-none"
              value={verticalValue}
              onChange={(event) => onVerticalValueChange(event.target.value)}
              title="Vertical split left bottom panel"
            >
              {splitOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          {actions}
        </div>
      </div>
      {navigation ? <div className="shrink-0 border-b bg-muted/20 px-3 py-1.5">{navigation}</div> : null}

      <div className="flex min-h-0 flex-1">
        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex h-8 shrink-0 items-center gap-2 border-b bg-slate-50 px-3 text-xs font-medium text-muted-foreground">
            <span className="text-foreground">{mainTitle}</span>
          </div>
          {mainFilter ? <div className="z-20 shrink-0 border-b bg-muted/30 px-2 py-1.5">{mainFilter}</div> : null}
          <div className="min-h-0 flex-1 overflow-hidden">{main}</div>
          {hasBottomPanel ? (
            <>
              <button
                type="button"
                className="h-1.5 shrink-0 cursor-row-resize bg-border transition-colors hover:bg-primary/30"
                onMouseDown={startBottomDrag}
                title="Drag vertical splitter"
              >
                <span className="flex h-full w-full items-center justify-center">
                  <GripHorizontal className="h-4 w-4 text-muted-foreground" />
                </span>
              </button>
              <section className="flex min-h-0 shrink-0 flex-col border-t bg-background" style={{ height: bottomSize }}>
                <div className="flex h-8 shrink-0 items-center gap-2 border-b bg-slate-50 px-3 text-xs font-medium text-muted-foreground">
                  <span className="text-foreground">{bottomTitle}</span>
                </div>
                {bottomFilter ? <div className="z-20 shrink-0 border-b bg-muted/30 px-2 py-1.5">{bottomFilter}</div> : null}
                <div className="min-h-0 flex-1 overflow-hidden">{bottomPanel}</div>
              </section>
            </>
          ) : null}
        </section>

        {hasRightPanel ? (
          <>
            <button
              type="button"
              className="w-1.5 shrink-0 cursor-col-resize bg-border transition-colors hover:bg-primary/30"
              onMouseDown={startRightDrag}
              title="Drag horizontal splitter"
            >
              <span className="flex h-full w-full items-center justify-center">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </span>
            </button>
            <section className="flex min-h-0 min-w-0 shrink-0 flex-col border-l bg-background" style={{ width: rightSize }}>
              <div className="flex h-8 shrink-0 items-center gap-2 border-b bg-slate-50 px-3 text-xs font-medium text-muted-foreground">
                <span className="text-foreground">{rightTitle}</span>
              </div>
              {rightFilter ? <div className="z-20 shrink-0 border-b bg-muted/30 px-2 py-1.5">{rightFilter}</div> : null}
              <div className="min-h-0 flex-1 overflow-hidden">{rightPanel}</div>
            </section>
          </>
        ) : null}
      </div>

      {footer ? <div className="shrink-0 border-t bg-muted/20 px-3 py-1.5">{footer}</div> : null}
    </div>
  );
}
